import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { usePanelStore } from "@/stores/usePanelStore";
import { useAuth } from "@/hooks/useAuth";
import * as panelStorage from "@/lib/agent/storage/panel-storage";
import type { AgentRun } from "@/lib/agent/types";

/**
 * Computes active human task counts per session.
 * - Active session: derived instantly from Zustand store
 * - Inactive sessions: batch-queried from IndexedDB, cached by TanStack Query
 */
export function useSessionTaskCounts(
  runs: AgentRun[],
  activeRunId: string | null,
): Map<string, number> {
  const tiles = usePanelStore((s) => s.tiles);
  const { userId } = useAuth();

  // Active session count — derived from Zustand (instant, reactive)
  const activeCount = useMemo(() => {
    if (!activeRunId) return 0;
    let count = 0;
    for (const tile of tiles) {
      if (tile.type !== "human_task") continue;
      try {
        const parsed = JSON.parse(tile.content);
        const status = parsed.status ?? "pending";
        if (status === "pending" || status === "in_progress") count++;
      } catch {
        count++;
      }
    }
    return count;
  }, [activeRunId, tiles]);

  // Inactive session IDs
  const inactiveRunIds = useMemo(
    () => runs.filter((r) => r.id !== activeRunId).map((r) => r.id),
    [runs, activeRunId],
  );

  // Batch query for inactive sessions — scoped by userId
  const { data: inactiveCounts } = useQuery({
    queryKey: ["session-task-counts", userId, inactiveRunIds],
    queryFn: () => panelStorage.getActiveTaskCountsByRuns(inactiveRunIds),
    enabled: inactiveRunIds.length > 0 && !!userId,
    staleTime: 30_000, // refresh every 30s
  });

  // Merge active + inactive counts
  return useMemo(() => {
    const result = new Map<string, number>(inactiveCounts ?? []);
    if (activeRunId && activeCount > 0) {
      result.set(activeRunId, activeCount);
    }
    return result;
  }, [activeRunId, activeCount, inactiveCounts]);
}
